import { SourceDefinition } from '../models/source-definition.model'
import { API_SOURCE, GridRole, SourceKey } from '../models/source-key.model'

/**
 * The single source of truth for what each source is and does. Everything downstream (source
 * picker, ColDef factory, match-count columns, footer, context menu) reads from here, so
 * adding or rewiring a source is a data change, not a code change.
 *
 * Column catalogues no longer live here (PVAL-26146): they come from the Flex Positions
 * field-catalog API via FieldCatalogService. Each source instead declares capability flags the
 * computed-column builder and grid read.
 */
export const SOURCE_REGISTRY: Record<SourceKey, SourceDefinition> = {
  api: {
    key: 'api',
    label: API_SOURCE.name,
    tag: 'API',
    tagColor: '#7d5fff',
    color: '#7d5fff',
    model: 'Position model',
    sub: `Connected via ${API_SOURCE.transport}`,
    allowedRoles: ['base', 'comparison'],
    // Enfusion is the Base feed for now; comparing it against itself in the top grid is not a
    // pairing we are ready to show, so the option is listed but greyed out.
    disabledRoles: ['comparison'],
    matchTargets: ['cust', 'trad', 'cw'],
    hasSourceColumn: true,
    hasLeadingAccountId: true,
    hasServerSideQuery: true,
    // Enfusion is the raw feed: no comments, ignore, transfer or security mapping
    // (Positions-2.0-Feature-Differences.md §4).
    hasComments: false,
  },
  prom: {
    key: 'prom',
    label: 'Prometheus',
    tag: 'EXT',
    tagColor: '#217dfe',
    color: '#1162e7',
    model: 'Normalized',
    sub: 'Custody + Trading',
    allowedRoles: ['comparison'],
    matchTargets: ['api', 'cw'],
    hasAggregateInfo: true,
    // The status chips live with Ignore, on the one grid that can produce an ignored row
    // (Positions-2.0-Feature-Differences.md §3 lists status chips as Prometheus).
    hasStatusFilter: true,
    hasIgnore: true,
    hasComments: true,
    hasSubSource: true,
    hasSourceColumn: true,
    hasServerSideQuery: true,
  },
  cw: {
    key: 'cw',
    label: 'Clearwater',
    tag: 'CORE',
    tagColor: '#0047d1',
    color: '#0047d1',
    model: 'ABOR',
    sub: 'Golden copy',
    allowedRoles: ['base'],
    // Clearwater is not cleared to drive the Base grid yet, so the Base grid stays on Enfusion.
    disabledRoles: ['base'],
    matchTargets: ['cust', 'trad', 'api'],
    hasComments: true,
    hasUnitDifferenceColumns: true,
    hasClosedPositionsSetting: true,
    hasQuickFilters: true,
  },
}

export const sourceDef = (key: SourceKey): SourceDefinition =>
  SOURCE_REGISTRY[key]

/**
 * Whether a grid in `role` may actually be pointed at `key` — allowed for the role, and not one of
 * the pairings held back for now.
 *
 * The single answer both the picker and the store use, so a disabled option cannot be reached by
 * clicking it, by a stale persisted source, or by a match-count cross-filter.
 */
export function isSourceSelectable(role: GridRole, key: SourceKey): boolean {
  const definition = SOURCE_REGISTRY[key]
  return (
    definition.allowedRoles.includes(role) &&
    !(definition.disabledRoles ?? []).includes(role)
  )
}
